import React, { useState, useCallback } from 'react';
import { BaseAccountForm } from './BaseAccountForm';
import { AccountFormField } from '../ui/AccountFormField';
import { validateEmail } from '../../utils/accountValidation';
import { ValidationResult } from '../../utils/validation';
import { useEmailChange } from '../../hooks/useEmailChange';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';

export interface EmailChangeFormProps {
  currentEmail: string;
  onSuccess: () => void;
  onError: (error: string) => void;
}

interface EmailChangeFormState {
  newEmail: string;
  isSubmitting: boolean;
  validationError: string | null;
  verificationSent: boolean;
}

export const EmailChangeForm: React.FC<EmailChangeFormProps> = ({
  currentEmail,
  onSuccess,
  onError
}) => {
  const [state, setState] = useState<EmailChangeFormState>({
    newEmail: '',
    isSubmitting: false,
    validationError: null,
    verificationSent: false
  });

  const [success, setSuccess] = useState<string | null>(null);
  const { changeEmail, isSubmitting: apiSubmitting, error: apiError, verificationSent: apiVerificationSent } = useEmailChange();

  // Handle email input change
  const handleEmailChange = useCallback((value: string) => {
    setState(prev => ({
      ...prev,
      newEmail: value,
      validationError: null
    }));
    
    // Clear any previous success messages when user starts typing
    if (success) {
      setSuccess(null);
    }
  }, [success]);

  // Handle email validation
  const handleEmailValidation = useCallback((result: ValidationResult) => {
    setState(prev => ({
      ...prev,
      validationError: result.isValid ? null : (result.error || 'Invalid email')
    }));
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous messages
    setSuccess(null);

    // Validate email before submission
    const emailValidation = validateEmail(state.newEmail);
    if (!emailValidation.isValid) {
      setState(prev => ({
        ...prev,
        validationError: emailValidation.error || 'Invalid email'
      }));
      return;
    }

    // Check if email is the same as current
    if (state.newEmail.trim().toLowerCase() === currentEmail.toLowerCase()) {
      onError('New email must be different from your current email');
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const result = await changeEmail(state.newEmail);
      
      if (result.success) {
        setState(prev => ({
          ...prev,
          isSubmitting: false,
          verificationSent: true
        }));
        
        setSuccess(result.message);
        onSuccess();
      } else {
        setState(prev => ({ ...prev, isSubmitting: false }));
        onError(result.message);
      }
    } catch (err) {
      setState(prev => ({ ...prev, isSubmitting: false }));
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to change email';
      onError(errorMessage);
    }
  }, [state.newEmail, currentEmail, changeEmail, onSuccess, onError]);

  // Clear success message
  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  // Check if form is valid for submission
  const isFormValid = state.newEmail.trim() && 
                     !state.validationError && 
                     state.newEmail.trim().toLowerCase() !== currentEmail.toLowerCase();

  // Combine local and API states
  const isSubmitting = state.isSubmitting || apiSubmitting;
  const verificationSent = state.verificationSent || apiVerificationSent;

  return (
    <BaseAccountForm
      title="Change Email Address"
      description="Update your email address. You'll need to verify the new email before the change takes effect."
      icon={<Mail className="w-5 h-5" />}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitText={verificationSent ? "Verification Sent" : "Send Verification Email"}
      submitDisabled={!isFormValid || verificationSent}
      error={apiError}
      success={success}
      onClearSuccess={clearSuccess}
    >
      {/* Current Email Display */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-muted-foreground">
          Current Email
        </label>
        <div className="px-3 py-2 bg-muted/50 border border-border rounded-md text-sm font-mono">
          {currentEmail}
        </div>
      </div>

      {/* New Email Input */}
      <AccountFormField
        id="new-email"
        label="New Email Address"
        type="email"
        value={state.newEmail}
        onChange={handleEmailChange}
        onValidation={handleEmailValidation}
        validator={validateEmail}
        placeholder="Enter your new email address"
        disabled={isSubmitting || verificationSent}
        required
        autoComplete="email"
        helperText="Make sure you have access to this email address"
      />

      {/* Verification Status */}
      {verificationSent && (
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Verification Email Sent
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Check your inbox at <span className="font-mono">{state.newEmail}</span> and click the verification link to complete the email change.
            </p>
          </div>
        </div>
      )}

      {/* Important Notice */}
      {!verificationSent && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
          <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
              Important
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300">
              You'll need to verify your new email address before the change takes effect. Make sure you have access to the email you're changing to.
            </p>
          </div>
        </div>
      )}
    </BaseAccountForm>
  );
};